import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api, apiErr, inr } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import { useAuth } from "@/context/AuthContext";
import { getPrinterConfig, isPrinterConnected, printReceipt } from "@/lib/escpos";
import AdminPOSHome from "@/pages/admin/AdminPOSHome";
import { POS_T } from "@/lib/posI18n";
import { PREMIUM_PLANS } from "@/lib/posPlans";
import { PosBanners } from "@/components/admin-pos/PosBanners";
import { ProductPanel } from "@/components/admin-pos/ProductPanel";
import { BillPanel } from "@/components/admin-pos/BillPanel";
import { PosModals } from "@/components/admin-pos/PosModals";

const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
const QUEUE_KEY = "rsmart_pos_queue";
const HOLD_KEY = "rsmart_pos_holds";
const round2 = (n) => Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100;
const getLS = (k) => { try { return JSON.parse(localStorage.getItem(k) || "[]"); } catch { return []; } };
const setLS = (k, v) => localStorage.setItem(k, JSON.stringify(v));

export default function AdminPOS() {
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [cats, setCats] = useState([]);
  const [storeId, setStoreId] = useState("store_main");
  const [catId, setCatId] = useState("");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [cust, setCust] = useState({ phone: "", name: "", due: 0 });
  const [billDisc, setBillDisc] = useState(0);
  const [billDiscPct, setBillDiscPct] = useState(0);
  const [extra, setExtra] = useState(0);
  const [roundOff, setRoundOff] = useState(0);
  const [payMode, setPayMode] = useState("single");
  const [selMethod, setSelMethod] = useState("cash");
  const [split, setSplit] = useState({ cash: 0, card: 0, upi: 0, credit: 0 });
  const [tendered, setTendered] = useState("");
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(getLS(QUEUE_KEY).length);
  const [holds, setHolds] = useState(getLS(HOLD_KEY));
  const [payOpen, setPayOpen] = useState(false);
  const [zOpen, setZOpen] = useState(false);
  const [zData, setZData] = useState(null);
  const [biz, setBiz] = useState(null);
  const [saleDone, setSaleDone] = useState(null);
  const [printerOpen, setPrinterOpen] = useState(false);
  const [pcfg, setPcfg] = useState(getPrinterConfig());
  const { subscribe } = useRealtime();
  const searchRef = useRef(null);
  const [view, setView] = useState("home");
  const [lang, setLang] = useState(localStorage.getItem("pos_lang") || "en");
  const setLangP = (l) => { localStorage.setItem("pos_lang", l); setLang(l); };
  const t = (k) => POS_T[lang]?.[k] ?? POS_T.en[k] ?? k;
  const [qcRate, setQcRate] = useState("");
  const [qcBuy, setQcBuy] = useState("");
  const [qcGst, setQcGst] = useState("");
  const [parties, setParties] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState("");
  const [defaultBiz, setDefaultBiz] = useState("");
  const [creditTerms, setCreditTerms] = useState(0);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [plan, setPlan] = useState("yearly");
  const plans = PREMIUM_PLANS.map((p) => ({ ...p, price: Number(biz?.[`plan_${p.id}`]) || p.price }));
  const selPlan = plans.find((p) => p.id === plan) || plans[1];
  const upiLink = (() => {
    if (!biz?.upi_id) return "";
    const note = `RsMart POS Premium - ${selPlan.name}`;
    return `upi://pay?pa=${encodeURIComponent(biz.upi_id)}&pn=${encodeURIComponent(biz?.name || "Rani Super Market")}&am=${selPlan.price}&tn=${encodeURIComponent(note)}&cu=INR`;
  })();

  const load = useCallback(() => api.get(`/products?limit=500&store_id=${storeId}`).then((r) => setProducts(r.data)), [storeId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/stores").then((r) => setStores(r.data)); api.get("/categories").then((r) => setCats(r.data)); api.get("/admin/business-settings").then((r) => setBiz(r.data)).catch(() => {}); api.get("/pos/holds").then((r) => setHolds(r.data)).catch(() => {}); api.get("/pos/parties").then((r) => setParties(r.data)).catch(() => {}); api.get("/pos/agents").then((r) => setAgents(r.data || [])).catch(() => {}); api.get("/admin/default-business").then((r) => setDefaultBiz(r.data?.business_id || "")).catch(() => {}); }, []);
  useEffect(() => subscribe((m) => { if (m.event?.startsWith("stock") || m.event?.startsWith("product")) load(); }), [subscribe, load]);

  const syncQueue = useCallback(async () => {
    let queue = getLS(QUEUE_KEY);
    if (!queue.length) return;
    const remaining = []; let dropped = 0;
    for (const sale of queue) {
      try { await api.post("/pos/bill", sale); }
      catch (e) { if (e?.response?.status === 409) dropped += 1; else remaining.push(sale); }
    }
    setLS(QUEUE_KEY, remaining); setQueued(remaining.length);
    const synced = queue.length - remaining.length - dropped;
    if (synced > 0) { toast.success(`${synced} offline bill(s) synced`); load(); }
    if (dropped > 0) toast.error(`${dropped} bill(s) could not sync — items went out of stock.`);
  }, [load]);

  useEffect(() => {
    const goOnline = () => { setOnline(true); syncQueue(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    if (navigator.onLine) syncQueue();
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, [syncQueue]);

  // ---------- cart ops ----------
  const add = (p) => setCart((c) => {
    const e = c.find((i) => i.product_id === p.id);
    if (e) return c.map((i) => i.product_id === p.id ? { ...i, quantity: round2(i.quantity + (p.sold_by_weight ? 0.5 : 1)) } : i);
    return [...c, { product_id: p.id, name: p.name, price: p.price, mrp: p.mrp, unit: p.unit, tax_rate: p.tax_rate ?? 5, image: p.image, stock: p.stock, sold_by_weight: !!p.sold_by_weight, hsn: p.hsn, quantity: p.sold_by_weight ? 0.5 : 1, discount: 0 }];
  });
  const setQty = (id, d) => setCart((c) => c.map((i) => i.product_id === id ? { ...i, quantity: Math.max(i.sold_by_weight ? 0.01 : 1, round2(i.quantity + d)) } : i));
  const setField = (id, k, v) => setCart((c) => c.map((i) => i.product_id === id ? { ...i, [k]: v } : i));
  const remove = (id) => setCart((c) => c.filter((i) => i.product_id !== id));

  const resetBill = () => { setCart([]); setCust({ phone: "", name: "", due: 0 }); setBillDisc(0); setBillDiscPct(0); setExtra(0); setRoundOff(0); setTendered(""); setSplit({ cash: 0, card: 0, upi: 0, credit: 0 }); setPayMode("single"); setSelMethod("cash"); setCreditTerms(0); };

  // ---------- totals ----------
  const gross = round2(cart.reduce((s, i) => s + i.price * i.quantity, 0));
  const itemDisc = round2(cart.reduce((s, i) => s + (Number(i.discount) || 0), 0));
  const lineBase = (i) => round2(i.price * i.quantity - (Number(i.discount) || 0));
  const sumBase = round2(cart.reduce((s, i) => s + lineBase(i), 0));
  const billDiscAmt = round2(Math.min(sumBase, (Number(billDisc) || 0) + sumBase * (Number(billDiscPct) || 0) / 100));
  let taxable = 0, gst = 0;
  cart.forEach((i) => { const base = lineBase(i); const share = sumBase > 0 ? billDiscAmt * base / sumBase : 0; const tx = base - share; taxable += tx; gst += tx * (i.tax_rate || 0) / 100; });
  taxable = round2(taxable); gst = round2(gst);
  const preRound = round2(gross - (itemDisc + billDiscAmt) + gst + (Number(extra) || 0));
  const total = round2(preRound + (Number(roundOff) || 0));
  const autoRound = () => setRoundOff(round2(Math.round(preRound) - preRound));

  const splitSum = round2(split.cash + split.card + split.upi + split.credit);
  const creditAmt = payMode === "single" ? (selMethod === "credit" ? total : 0) : round2(split.credit);
  const cashAmt = payMode === "single" ? (selMethod === "cash" ? total : 0) : round2(split.cash);
  const change = tendered ? round2(Math.max(0, Number(tendered) - cashAmt)) : 0;

  const filtered = products.filter((p) => (!catId || p.category_id === catId) && (!q || p.name.toLowerCase().includes(q.toLowerCase()) || p.sku?.toLowerCase().includes(q.toLowerCase()) || p.barcode?.includes(q)));

  // ---------- customer lookup ----------
  const lookup = async (phone) => {
    setCust((c) => ({ ...c, phone }));
    const digits = (phone || "").replace(/\D/g, "");
    if (digits.length >= 10) {
      try { const { data } = await api.get(`/pos/customer-lookup?phone=${encodeURIComponent(phone)}`); setCust({ phone, name: data.name || "", due: data.due || 0 }); } catch (e) { console.warn("customer-lookup failed", e); }
    }
  };

  // ---------- hold / park (synced to backend so any counter can resume) ----------
  const buildHold = (hid) => ({ id: hid, at: new Date().toISOString(), cart, cust, billDisc, billDiscPct, extra, label: cust.name || cust.phone || "Bill", total, store_id: storeId });
  const holdBill = async () => {
    if (!cart.length) return;
    const hid = uuid();
    try {
      await api.put(`/pos/holds/${hid}`, buildHold(hid));
      setHolds((h) => [...h, buildHold(hid)]); resetBill(); toast.success("Bill held (synced — kisi bhi counter se resume)");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const resumeHold = async (h) => {
    setCart(h.cart); setCust(h.cust); setBillDisc(h.billDisc || 0); setBillDiscPct(h.billDiscPct || 0); setExtra(h.extra || 0);
    setHolds((x) => x.filter((y) => y.id !== h.id));
    try { await api.delete(`/pos/holds/${h.id}`); } catch (e) { console.warn("resume-hold delete failed", e); }
  };
  const dropHold = async (id) => {
    setHolds((x) => x.filter((y) => y.id !== id));
    try { await api.delete(`/pos/holds/${id}`); } catch (e) { console.warn("drop-hold delete failed", e); }
  };

  // ---------- checkout ----------
  const doCheckout = async () => {
    if (!cart.length) return;
    let payments;
    if (payMode === "single") payments = [{ method: selMethod, amount: total }];
    else {
      payments = [["cash", split.cash], ["card", split.card], ["upi", split.upi], ["credit", split.credit]].filter(([, a]) => a > 0).map(([method, amount]) => ({ method, amount: round2(amount) }));
      if (round2(payments.reduce((s, p) => s + p.amount, 0)) !== total) return toast.error(`Split total must equal ${inr(total)}`);
    }
    if (creditAmt > 0 && (cust.phone || "").replace(/\D/g, "").length < 10) return toast.error("Credit (udhaar) sale ke liye customer phone chahiye");
    const bill = {
      items: cart.map((i) => ({ product_id: i.product_id, quantity: Number(i.quantity), price: Number(i.price), discount: Number(i.discount) || 0 })),
      store_id: storeId, customer_name: cust.name || null, customer_phone: cust.phone || null,
      bill_discount: Number(billDisc) || 0, bill_discount_percent: Number(billDiscPct) || 0,
      extra_charge: Number(extra) || 0, round_off: Number(roundOff) || 0,
      payments, tendered: Number(tendered) || 0, credit_terms: creditAmt > 0 ? Number(creditTerms) || 0 : 0, idempotency_key: uuid(),
      agent_id: agentId || null, business_id: defaultBiz || null,
    };
    if (!navigator.onLine) {
      const queue = [...getLS(QUEUE_KEY), bill]; setLS(QUEUE_KEY, queue); setQueued(queue.length);
      toast.success("Offline — bill saved & will sync when back online"); setPayOpen(false); resetBill(); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/pos/bill", bill);
      toast.success(`Invoice ${data.invoice_no} · ${inr(data.total)}`);
      setPayOpen(false); load();
      if (isPrinterConnected() && biz) printReceipt(data, biz, pcfg.width).catch((err) => toast.error("Printer: " + err.message));
      setSaleDone({ ...data, _phone: bill.customer_phone });
      resetBill();
    } catch (e) {
      if (!e.response) { const queue = [...getLS(QUEUE_KEY), bill]; setLS(QUEUE_KEY, queue); setQueued(queue.length); toast.success("Connection lost — bill queued offline"); setPayOpen(false); resetBill(); }
      else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  // ---------- customer prefill from ?phone=&name= (from header quick-add) ----------
  useEffect(() => {
    const prm = new URLSearchParams(window.location.search);
    const ph = prm.get("phone");
    if (ph) { lookup(ph); if (prm.get("name")) setCust((c) => ({ ...c, name: prm.get("name") })); window.history.replaceState({}, "", "/admin/pos"); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { user } = useAuth();
  const reloadBiz = () => api.get("/admin/business-settings").then((r) => setBiz(r.data)).catch(() => {});
  const activatePremium = async () => {
    try { await api.post("/admin/premium/activate", { plan }); toast.success(`Premium activated · ${selPlan.name}`); setUpgradeOpen(false); reloadBiz(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openPay = () => { if (!cart.length) return toast.error("Cart khaali hai"); if (biz?.trial_expired) return toast.error("Trial khatam — Premium lein"); setSplit((s) => ({ ...s, cash: total, card: 0, upi: 0, credit: 0 })); setTendered(""); setPayOpen(true); };

  // ---------- keyboard shortcuts ----------
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "F2") { e.preventDefault(); payOpen ? doCheckout() : openPay(); }
      else if (e.key === "F4") { e.preventDefault(); holdBill(); }
      else if (e.key === "F3") { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const onSearchEnter = (e) => {
    if (e.key !== "Enter") return;
    const exact = products.find((p) => p.barcode && p.barcode === q.trim());
    if (exact) { add(exact); setQ(""); return; }
    if (filtered.length === 1) { add(filtered[0]); setQ(""); }
  };

  const openZ = async () => {
    try { const { data } = await api.get(`/admin/pos/day-summary?store_id=${storeId}`); setZData(data); setZOpen(true); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const doQuickCreate = async () => {
    const name = q.trim();
    if (!name) return;
    try {
      const { data } = await api.post("/pos/quick-product", { name, price: Number(qcRate) || 0, cost_price: Number(qcBuy) || 0, tax_rate: Number(qcGst) || 0, stock: 999, store_id: storeId, category_id: catId || undefined });
      add(data); setQ(""); setQcRate(""); setQcBuy(""); setQcGst("");
      load(); toast.success(`${data.name} ban gaya & bill me add`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const resumeHoldHome = async (h) => { await resumeHold(h); setView("sale"); };

  if (view === "home") return <AdminPOSHome lang={lang} setLang={setLangP} onNewSale={() => setView("sale")} onDayClose={openZ} onOpenPrinter={() => setPrinterOpen(true)} holds={holds} onResumeHold={resumeHoldHome} onDropHold={dropHold} online={online} queued={queued} onSync={syncQueue} />;

  return (
    <div className="grid lg:grid-cols-3 gap-5 h-full">
      <PosBanners biz={biz} selPlan={selPlan} setUpgradeOpen={setUpgradeOpen} />
      <ProductPanel p={{
        t, setView, stores, storeId, setStoreId, openZ, setPrinterOpen, online, queued, syncQueue,
        searchRef, q, setQ, onSearchEnter, cats, catId, setCatId, filtered, add,
        doQuickCreate, qcRate, setQcRate, qcBuy, setQcBuy, qcGst, setQcGst,
      }} />
      <BillPanel p={{
        cart, cust, lookup, setCust, parties, agents, agentId, setAgentId, holds, resumeHold, dropHold,
        setQty, setField, remove, billDisc, setBillDisc, billDiscPct, setBillDiscPct, extra, setExtra,
        roundOff, setRoundOff, autoRound, gross, itemDisc, billDiscAmt, taxable, gst, total, busy, openPay,
        holdBill, resetBill,
      }} />
      <PosModals m={{
        payOpen, setPayOpen, total, payMode, setPayMode, selMethod, setSelMethod,
        tendered, setTendered, change, split, setSplit, splitSum, creditAmt,
        creditTerms, setCreditTerms, t, busy, doCheckout,
        zOpen, setZOpen, zData,
        saleDone, setSaleDone,
        upgradeOpen, setUpgradeOpen, plans, plan, setPlan, selPlan, upiLink,
        biz, user, activatePremium,
        printerOpen, setPrinterOpen, pcfg, setPcfg,
      }} />
    </div>
  );
}
